import {
  ContentContainer,
  EllipsedText,
  FlexRow,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { TENANTS_TABLE_COLUMNS_NAME } from '../../constants/tenants';
import { useStyles } from '../../hooks/useStyles';
import { getTenants } from '../../services/tenantsService';
import { Tenant } from '../../types/tenants';
import { TableWrapper } from '../shared/TableWrapper';
import { TenantModal } from '../tenants/TenantModal';
import StyledTooltip from '../ui/StyledTooltip';

export function TenantsListPage() {
  const { css, theme, classes } = useStyles();
  const [tenants, setTenants] = useState<Tenant[]>();
  const [selectedTenant, setSelectedTenant] = useState<Tenant>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number | undefined>(undefined);
  const [totalItems, setTotalItems] = useState(0);
  const { search, setSearch } = useDebouncedSearch(useState<string>());
  const { openModal, closeModal, showModal } = useModal();

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (page === undefined) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadTenants = async () => {
      const { paginatedResponse, error } = await getTenants(page, search);
      if (!error && paginatedResponse) {
        setTenants(paginatedResponse.items);
        setTotalItems(paginatedResponse.total);
      } else {
        setTenants(undefined);
        setTotalItems(0);
      }
      stopLoading();
    };
    loadTenants();
  }, [page]);

  const onUpdate = () => {
    setPage(0);
  };

  const onOpenModal = (tenant?: Tenant) => {
    setSelectedTenant(tenant);
    openModal();
  };

  const onSubmitFinished = () => {
    onUpdate();
    onCloseModal();
  };

  const onCloseModal = () => {
    setSelectedTenant(undefined);
    closeModal();
  };

  return (
    <ContentContainer>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={classes.flexible(1)}>
          <StyledSearchBar
            name="tenant-search"
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <FlexRow
          gap={0}
          classNames={css({ alignItems: 'center' })}
        >
          <StyledTooltip
            content={'Nuevo'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={() => onOpenModal()}
              kind="tertiary"
            >
              <MaterialIcon name={'add'} />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content={'Actualizar'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={onUpdate}
              kind="tertiary"
            >
              <MaterialIcon name={'sync'} />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
      <TableWrapper
        columns={TENANTS_TABLE_COLUMNS_NAME}
        data={tenants?.map((tenant, i) => {
          return {
            items: [
              <EllipsedText key={`tenant-name-${i}`}>{tenant.name}</EllipsedText>,
              <EllipsedText key={`tenant-social-${i}`}>{tenant.socialReason ?? '-'}</EllipsedText>,
              <EllipsedText key={`tenant-status-${i}`}>{tenant.active ? 'Si' : 'No'}</EllipsedText>,
              <FlexRow key={`tenant-actions-${i}`}>
                <StyledTooltip
                  content={'Nuevo'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={() => onOpenModal(tenant)}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="xs-mini"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>,
            ],
          };
        })}
        hasData={tenants !== undefined}
        isEmpty={!tenants?.length}
        loading={loading}
        noDataDescription="Aun no tienes ningun cliente"
        onPageChange={setPage}
        page={page ?? 0}
        totalItems={totalItems}
        hasSearch
      />
      <TenantModal
        isOpen={showModal}
        onClose={onCloseModal}
        onSubmitFinished={onSubmitFinished}
        tenant={selectedTenant}
        tenantId={selectedTenant?._id}
      />
    </ContentContainer>
  );
}
